import React from 'react';
import { Form, Input, Radio, Table, Select, Checkbox } from 'antd';

const CheckDetailForm = (props) => {
    const {
        listName,
        data,
        columns = [],
        requiredColWidth = '300px',
        placeholder = "Ghi rõ chi tiết cụ thể tại đây",
        radioLabels = { no: "Không", yes: "Có (ghi rõ chi tiết)" },
        triggerValues = ['yes'], // Giá trị mặc định sẽ hiển thị TextArea
        required = false,
        ...rest
    } = props;

    // Theo dõi toàn bộ list values trong Form (tự re-render khi form thay đổi)
    const listValues = Form.useWatch(listName) || [];
    const form = Form.useFormInstance();

    // Helper: kiểm tra có cần hiển thị detail không ở index i
    const shouldShowDetail = (record, i) => {
        const row = listValues?.[i] || {};
        const value = row.has_occurrence ?? record.has_occurrence;
        const detail = (row.occurrence_detail ?? record.occurrence_detail) || '';
        const triggers = record.triggerValues || triggerValues;

        const byTrigger = Array.isArray(value)
            ? value?.some(v => triggers.includes(v))
            : triggers.includes(value);

        const byDetail = String(detail).trim() !== '';

        return byTrigger || byDetail;
    };

    /**
     * Xử lý khi giá trị thay đổi cho tất cả loại input
     */
    const handleValueChange = (index, value, record) => {
        const triggers = record.triggerValues || triggerValues;
        const currentList = form.getFieldValue(listName) || [];

        // Nếu người dùng clear (bỏ chọn) → null
        if (value === undefined || value === '' || (Array.isArray(value) && value.length === 0)) {
            currentList[index] = { has_occurrence: null, occurrence_detail: null };
        } else {
            const isTriggered = Array.isArray(value)
                ? value.some(v => triggers.includes(v))
                : triggers.includes(value);

            if (!isTriggered) {
                // Nếu chọn giá trị không nằm trong trigger → xóa detail
                currentList[index] = { has_occurrence: value, occurrence_detail: null };
            } else {
                // Nếu có trigger → giữ lại giá trị
                const prev = currentList[index] || {};
                currentList[index] = { ...prev, has_occurrence: value };
            }
        }

        form.setFieldsValue({ [listName]: currentList });
    };

    // const handleValueChange = (index, value, record) => {
    //     const triggers = record.triggerValues || triggerValues;
    //     const isTriggered = Array.isArray(value)
    //         ? value.some(v => triggers.includes(v))
    //         : triggers.includes(value);

    //     if (!isTriggered) {
    //         // Khi người dùng chọn giá trị không thuộc triggerValues,
    //         // ẩn TextArea và xóa nội dung detail trong form
    //         const currentList = form.getFieldValue(listName) || [];
    //         if (currentList[index]) {
    //             currentList[index].occurrence_detail = '';
    //             form.setFieldsValue({ [listName]: currentList });
    //         }
    //     }
    // };

    /**
     * Hàm render linh hoạt cho từng loại input
     */
    const renderInputType = (record, index) => {
        const { inputType = "radio", options = [] } = record;

        switch (inputType) {
            case "rank":
                return (
                    <Form.Item
                        name={[index, "has_occurrence"]}
                        rules={[{ required: required, message: "Bắt buộc chọn" }]}
                    >
                        <Radio.Group onChange={(e) => handleValueChange(index, e.target.value, record)}>
                            {options.map((opt) => (
                                <Radio key={opt.value} value={opt.value}>
                                    {opt.label}
                                </Radio>
                            ))}
                        </Radio.Group>
                    </Form.Item>
                );

            case "select":
                return (
                    <Form.Item
                        name={[index, "has_occurrence"]}
                        rules={[{ required: required, message: "Bắt buộc chọn" }]}
                    >
                        <Select
                            placeholder="Chọn một lựa chọn"
                            onChange={(value) => handleValueChange(index, value, record)}
                            allowClear
                        >
                            {options.map((opt) => (
                                <Select.Option key={opt.value} value={opt.value}>
                                    {opt.label}
                                </Select.Option>
                            ))}
                        </Select>
                    </Form.Item>
                );

            case "checkbox":
                return (
                    <Form.Item
                        name={[index, "has_occurrence"]}
                        rules={[{ required: required, message: "Bắt buộc chọn" }]}
                    >
                        <Checkbox.Group
                            options={options}
                            onChange={(values) => handleValueChange(index, values, record)}
                        />
                    </Form.Item>
                );

            case "radio":
            default:
                return (
                    <Form.Item
                        name={[index, "has_occurrence"]}
                        initialValue={record.has_occurrence}
                        rules={[{ required: required, message: "Bắt buộc chọn" }]}
                    >
                        <Radio.Group onChange={(e) => handleValueChange(index, e.target.value, record)}>
                            <Radio value="no">{radioLabels.no}</Radio>
                            <Radio value="yes">{radioLabels.yes}</Radio>
                        </Radio.Group>
                    </Form.Item>
                );
        }
    };

    /**
     * Cột lựa chọn chính (radio/select/checkbox)
     */
    const requiredColumns = [
        {
            title: "",
            dataIndex: "has_occurrence",
            width: requiredColWidth,
            className: "occurrence-detail",
            render: (_, record, index) => renderInputType(record, index),
        },
    ];

    /**
     * Cột chi tiết (hiển thị TextArea nếu có trigger)
     */
    const occurrenceDetailColumn = {
        title: "",
        dataIndex: "occurrence_detail",
        className: "occurrence-detail",
        render: (_, record, index) =>
            shouldShowDetail(record, index) && (
                <Form.Item
                    name={[index, "occurrence_detail"]}
                    initialValue={record.occurrence_detail}
                    rules={[{ required: required, message: "Bắt buộc nhập" }]}
                >
                    <Input.TextArea rows={1} placeholder={placeholder} />
                </Form.Item>
            ),
    };

    // Giữ lại cột label do người dùng truyền vào
    const filteredColumns = columns.filter(col => col.dataIndex !== "occurrence_detail");

    // Hợp nhất toàn bộ cột
    let mergedColumns = [
        ...filteredColumns,
        ...requiredColumns,
        occurrenceDetailColumn,
    ];

    // Thêm className "occurrence-detail" cho tất cả
    mergedColumns = mergedColumns.map(col => ({
        ...col,
        className: col.className
            ? `${col.className} occurrence-detail`
            : "occurrence-detail",
    }));

    return (
        <Form.List name={listName}>
            {() => (
                <Table
                    className="no-border-table"
                    showHeader={false}
                    dataSource={data}
                    columns={mergedColumns}
                    rowKey={(record, index) => index}
                    pagination={false}
                    size="small"
                    bordered={false}
                    scroll={{ x: 'max-content' }}
                    locale={{ emptyText: "Chưa có dữ liệu" }}
                    {...rest}
                />
            )}
        </Form.List>
    );
};

export default CheckDetailForm;